import { applyTaskFilter, type TaskProgress } from "../helpers/task_progress";

declare const Alpine: { $persist<T>(value: T): { as(key: string): T } };

interface HomepageState {
  positionWatchId: number | null;
  searchQuery: string;
  campaignSearchQuery: string;
  campaignStatus: string[];
  showMap: boolean;
  showFilter: boolean;
  showSortMenu: boolean;
  fullScreenMode: boolean;
  sortByMode: string;
  placeMarkers: PlaceMarker[];
  campaignOptions: CampaignOption[];
  campaignFilterIds: number[];
  initMap: () => Promise<void>;
  startPositionTracking: () => void;
  stopPositionTracking: () => void;
  updateCurrentPosition: (position: Position) => void;
  refreshMap: () => void;
  hasResults: (
    items: (PlaceTask | PlaceMarker)[],
    searchQuery: string,
    campaignFilterIds: number[],
    campaignStatus: string[],
  ) => boolean;
  showItem: (
    item: PlaceTask | PlaceMarker,
    searchQuery: string,
    campaignFilterIds: number[],
    campaignStatus: string[],
  ) => boolean;
  countItems: (
    items: PlaceTask[],
    searchQuery: string,
    campaignFilterIds: number[],
    campaignStatus: string[],
  ) => number;
  setCampaignOptions: () => void;
  toggleCampaignFilterSelection: (id: number) => void;
  resetCampaignFilter: () => void;
  sortItems: (
    items: PlaceTask[],
    sortBy: string,
    geolocPermissionState: GeolocPermissionState,
  ) => PlaceTask[];
}

interface PlaceTask {
  id: number;
  task_count: number;
  success_task_count: number;
  distance: number;
  place_address: string;
  place_id: number;
  place_kind: string;
  place_name: string;
  place_zip_code: string;
  place_coordinates: Position;
  campaigns: Campaign[];
  requests: Request[];
}

interface PlaceMarker {
  type: string;
  place_id: number;
  place_name: string;
  place_address: string;
  place_zip_code: string;
  position: Position;
  campaigns: Campaign[];
  requests: Request[];
  selected_campaigns: Campaign[];
  progress: TaskProgress;
}

interface Campaign {
  id: number;
  task_id: number;
  name: string;
  due_date: string;
  scheduled_date: string;
  start_date: string;
  end_date: string;
  status: string;
}

interface Request {
  task_id: number;
  type: string;
  due_date: string;
  scheduled_date: string;
  status: string;
}

interface CampaignOption {
  id: number;
  name: string;
  pending_count: number;
}

interface GeolocPermissionState {
  status: string;
  position: Position;
}
interface MapMarker {
  marker: google.maps.marker.AdvancedMarkerElement;
  pin: google.maps.marker.PinElement;
  placeId: number;
}

interface Position {
  lat: number;
  lng: number;
}

let map: google.maps.Map | null = null;
let mapInitializing = false;
let currentPositionMarker: google.maps.marker.AdvancedMarkerElement | null = null;

const mapMarkers: MapMarker[] = [];
const completedTaskStatuses = ["completed", "locked", "archived"];

async function initMap(this: HomepageState, position?: Position) {
  if (map || mapInitializing) return;

  mapInitializing = true;

  try {
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary("marker");
    const mapElement = document.getElementById("map");

    if (!mapElement) {
      throw new Error("Map element not found");
    }

    map = new google.maps.Map(mapElement, {
      mapId: "task-map",
      maxZoom: 18,
      disableDefaultUI: true,
    });

    const infoWindow = new google.maps.InfoWindow({});
    const bounds = new google.maps.LatLngBounds();

    for (const placeMarker of this.placeMarkers) {
      const pin = new PinElement({
        glyphText: `${placeMarker.progress.total}`,
        glyphColor: "black",
        background: placeColor(placeMarker),
        borderColor: "#90A1B9",
      });

      const marker = new AdvancedMarkerElement({
        map: map,
        position: placeMarker.position,
        content: pin,
        title: placeMarker.place_name,
        gmpClickable: true,
      });

      const placeId = placeMarker.place_id;

      mapMarkers.push({ marker, pin, placeId: placeId });

      marker.addListener("gmp-click", () => {
        const currentPlaceMarker = this.placeMarkers.find((item) => item.place_id === placeId);

        if (!currentPlaceMarker) {
          return;
        }

        infoWindow.close();
        infoWindow.setContent(buildContent(currentPlaceMarker));
        infoWindow.open(marker.map, marker);
      });

      if (showItem(placeMarker, this.searchQuery, this.campaignFilterIds, this.campaignStatus)) {
        bounds.extend(placeMarker.position);
      } else {
        marker.map = null;
      }
    }

    if (position) {
      currentPositionMarker = createCurrentPositionMarker(map, position);
      bounds.extend(position);
    }

    map.fitBounds(bounds);
  } finally {
    mapInitializing = false;
  }
}

function placeColor(item: PlaceMarker) {
  if (item.progress.total === item.progress.completed) {
    return "#5FE9B5";
  } else if (item.progress.late > 0) {
    return "#FFA2A2";
  } else if (item.progress.scheduled > 0) {
    return "#FFD230";
  } else {
    return "#E2E8F0";
  }
}

function stopPositionTracking(this: HomepageState) {
  if (this.positionWatchId !== null) {
    navigator.geolocation.clearWatch(this.positionWatchId);
    this.positionWatchId = null;
  }
}

function startPositionTracking(this: HomepageState) {
  if (!navigator.geolocation) {
    return;
  }

  this.positionWatchId = navigator.geolocation.watchPosition(
    (pos) => {
      const position = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      this.updateCurrentPosition(position);
    },
    () => {},
    {
      enableHighAccuracy: true,
      timeout: 15_000,
      maximumAge: 0,
    },
  );
}

function updateCurrentPosition(this: HomepageState, position: Position) {
  if (!map) return;

  if (!currentPositionMarker) {
    currentPositionMarker = createCurrentPositionMarker(map, position);
  } else {
    currentPositionMarker.position = position;
  }
}

const createCurrentPositionMarker = (map: google.maps.Map, position: Position) => {
  const element = document.createElement("div");
  element.className =
    "current-position-marker h-4 w-4 rounded-full bg-info-btn shadow-md border-2 border-white";

  const marker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position,
    content: element,
    zIndex: 1000,
  });

  return marker;
};

function refreshMap(this: HomepageState) {
  this.placeMarkers = applyTaskFilter(
    this.placeMarkers,
    this.campaignFilterIds,
    this.campaignStatus,
  );
  refreshMapMarkers.call(this);
}

function refreshMapMarkers(this: HomepageState) {
  for (const mapMarker of mapMarkers) {
    const placeMarker = this.placeMarkers.find((item) => item.place_id === mapMarker.placeId);

    if (!placeMarker) {
      mapMarker.marker.map = null;
      continue;
    }

    mapMarker.pin.glyphText = `${placeMarker.progress.total}`;
    mapMarker.pin.background = placeColor(placeMarker);

    mapMarker.marker.map = showItem(
      placeMarker,
      this.searchQuery,
      this.campaignFilterIds,
      this.campaignStatus,
    )
      ? map
      : null;
  }
}

function hasResults(
  items: PlaceTask[],
  searchQuery: string,
  campaignFilterIds: number[],
  campaignStatus: string[],
): boolean {
  return items.some((item) => showItem(item, searchQuery, campaignFilterIds, campaignStatus));
}

function showItem(
  item: PlaceTask | PlaceMarker,
  searchQuery: string,
  campaignFilterIds: number[],
  campaignStatus: string[],
) {
  return (
    (item.place_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.place_zip_code?.includes(searchQuery) ||
      item.campaigns?.some((campaign) =>
        campaign.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )) &&
    (campaignFilterIds.length === 0 ||
      item.campaigns?.some((campaign) => campaignFilterIds.includes(campaign.id))) &&
    (campaignStatus.includes("all") ||
      item.campaigns?.some((campaign) => campaignStatus.includes(campaign.status)))
  );
}

function countItems(
  items: PlaceTask[],
  searchQuery: string,
  campaignFilterIds: number[],
  campaignStatus: string[],
) {
  return items.reduce((acc, item) => {
    if (showItem(item, searchQuery, campaignFilterIds, campaignStatus)) {
      return acc + filterTaskCount(item, campaignFilterIds, campaignStatus);
    }
    return acc;
  }, 0);
}

function filterTaskCount(item: PlaceTask, campaignFilterIds: number[], campaignStatus: string[]) {
  const campaignTasks = item.campaigns.filter(
    (campaign) =>
      (campaignFilterIds.length === 0 || campaignFilterIds.includes(campaign.id)) &&
      (campaignStatus.includes("all") || campaignStatus.includes(campaign.status)) &&
      !completedTaskStatuses.includes(campaign.status),
  );

  const requestTasks = item.requests.filter(
    (request) => campaignFilterIds.length === 0 && !completedTaskStatuses.includes(request.status),
  );

  return campaignTasks.length + requestTasks.length;
}

function sortItems(
  items: PlaceTask[],
  sortBy: string,
  geolocPermissionState: GeolocPermissionState,
) {
  const position = geolocPermissionState?.position;
  if (items && position) {
    items = items.map((item) => ({
      ...item,
      distance: haversineDistance(position, item.place_coordinates),
    }));
    if (sortBy === "distance") {
      return items.sort((a, b) => a.distance - b.distance);
    }
  }
  return items;
}

const haversineDistance = (coord1: Position, coord2: Position) => {
  const R = 6371; // Rayon de la Terre en kilomètres
  const toRad = (angle: number) => (angle * Math.PI) / 180;

  const lat1 = toRad(coord1.lat);
  const lng1 = toRad(coord1.lng);
  const lat2 = toRad(coord2.lat);
  const lng2 = toRad(coord2.lng);

  const dLat = lat2 - lat1;
  const dLng = lng2 - lng1;

  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = R * c;
  return distance; // en kilomètres
};

function setCampaignOptions(this: HomepageState) {
  const query = this.campaignSearchQuery.toLowerCase();
  const campaignOptions = new Map<number, CampaignOption>();

  this.placeMarkers.forEach(({ campaigns }) => {
    campaigns.forEach((campaign) => {
      if (
        campaign.name.toLowerCase().includes(query) &&
        (this.campaignStatus.includes("all") || this.campaignStatus.includes(campaign.status))
      ) {
        const existing = campaignOptions.get(campaign.id);

        if (existing) {
          if (!isCompletedTask(campaign)) {
            existing.pending_count++;
          }
          return;
        }

        campaignOptions.set(campaign.id, {
          id: campaign.id,
          name: campaign.name,
          pending_count: isCompletedTask(campaign) ? 0 : 1,
        });
      }
    });
  });

  this.campaignOptions = Array.from(campaignOptions.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "fr", { sensitivity: "base" }),
  );
}

function isCompletedTask(campaign: Campaign): boolean {
  return completedTaskStatuses.includes(campaign.status);
}

function toggleCampaignFilterSelection(this: HomepageState, id: number) {
  if (this.campaignFilterIds.includes(id)) {
    this.campaignFilterIds = this.campaignFilterIds.filter(
      (campaignId: number) => campaignId !== id,
    );
  } else {
    this.campaignFilterIds.push(id);
  }

  this.refreshMap();
}

function resetCampaignFilter(this: HomepageState) {
  this.campaignFilterIds = [];
  this.campaignSearchQuery = "";
  this.showFilter = false;
  this.campaignStatus = ["all"];

  this.refreshMap();
}

function toggleFullScreenMode(this: HomepageState, el: HTMLElement) {
  this.fullScreenMode = !this.fullScreenMode;
  el.classList.toggle("map-fullscreen");
}

function buildContent(placeMarker: PlaceMarker) {
  const content = document.createElement("div");
  content.className = "pr-2 pb-2";
  content.innerHTML = `
  <div class="flex items-center justify-between">
    <div class="absolute top-4 left-3">
      <a href="/places/${placeMarker.place_id}" class="flex items-center">
        <i class="fas fa-arrow-up-right-from-square text-default-txt text-lg"></i>
      </a>
    </div>
  </div>
  `;

  if (placeMarker.place_name) {
    content.innerHTML += `
      <div class="flex space-x-2 items-center">
        <i class="fat fa-location-dot text-xs"></i>
        <div class="task-place-name text-default-txt font-semibold text-sm group-hover:text-default-txt-hover">
        ${placeMarker.place_name}
        </div>
      </div>
      <a href="https://maps.google.com/?q=${placeMarker.position.lat},${placeMarker.position.lng}" class="task-place-address text-default-txt-informative text-xs">
        ${placeMarker.place_address}
      </a>
    `;
  } else {
    content.innerHTML += `
      <div class="flex space-x-2 items-center">
        <i class="fat fa-location-dot text-xs"></i>
        <a href="https://maps.google.com/?q=${placeMarker.position.lat},${placeMarker.position.lng}" class="task-place-name text-default-txt font-semibold text-sm group-hover:text-default-txt-hover">
        ${placeMarker.place_address}
        </a>
      </div>
    `;
  }

  if (placeMarker.selected_campaigns.length > 0) {
    content.innerHTML += `
      <div class="text-default-txt font-medium text-xs mt-4">
        Campagnes : ${placeMarker.selected_campaigns.length}
      </div>
    `;

    for (const campaign of placeMarker.selected_campaigns) {
      content.innerHTML += `
        <a href="/places/${placeMarker.place_id}/tasks/${campaign.task_id}">
          <div class="text-xs mt-1 font-normal truncate">
            ${campaign.name}
          </div>
        </a>
      `;
      if (campaign.start_date && campaign.end_date) {
        content.innerHTML += `
          <div class="text-xs text-default-txt-informative">
           du ${formatDate(campaign.start_date)} au ${formatDate(campaign.end_date)}
          </div>
        `;
      } else {
        content.innerHTML += `
          <div class="text-xs text-default-txt-informative">
            à partir du ${formatDate(campaign.start_date)}
          </div>
        `;
      }
    }
  }

  if (placeMarker.requests.length > 0) {
    content.innerHTML += `
      <div class="text-default-txt font-medium text-xs mt-4">
        Demandes : ${placeMarker.requests.length}
      </div>
    `;

    for (const request of placeMarker.requests) {
      content.innerHTML += `
        <a href="/places/${placeMarker.place_id}/tasks/${request.task_id}">
          <div class="text-xs mt-1 font-normal truncate">
            ${request.type}
          </div>
        </a>
      `;
      if (request.scheduled_date) {
        content.innerHTML += `
          <div class="text-xs text-default-txt-informative">
            programmée le ${formatDate(request.scheduled_date)}
          </div>
        `;
      }
    }
  }

  return content;
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("fr-FR");
}

function alpineData({ placeMarkers }: { placeMarkers: PlaceMarker[] }) {
  return {
    positionWatchId: null,
    searchQuery: "",
    campaignSearchQuery: "",
    showMap: false,
    showFilter: false,
    showSortMenu: false,
    fullScreenMode: false,
    campaignOptions: [],
    sortByMode: Alpine.$persist("date").as("sortby_mode"),
    campaignFilterIds: Alpine.$persist([]).as("campaign_filter_ids"),
    campaignStatus: Alpine.$persist(["all"]).as("campaign_status"),
    showMapKey: Alpine.$persist(true).as("show_map_key"),
    placeMarkers: placeMarkers,

    initMap,
    startPositionTracking,
    stopPositionTracking,
    updateCurrentPosition,
    refreshMap,
    hasResults,
    showItem,
    countItems,
    sortItems,
    setCampaignOptions,
    toggleCampaignFilterSelection,
    resetCampaignFilter,
    toggleFullScreenMode,

    init() {
      this.placeMarkers = applyTaskFilter(
        this.placeMarkers,
        this.campaignFilterIds,
        this.campaignStatus,
      );
      this.setCampaignOptions();
    },
  } as HomepageState;
}

export default alpineData;
